/**
 * Same-origin JSON client for /api/v1/desk (OD-65). The session lives only in an HttpOnly cookie; when
 * it lapses (1 h idle) the client signs a fresh server challenge with this device's key and retries
 * once — the agent never types anything. Every mutation carries X-HopEx-Request: 1 (CSRF defence).
 */
import { forgetDevice, loadDevice, signChallenge } from './device.ts';
import type { Me } from './types.ts';

export class ApiFailure extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

const OFFLINE = 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید.';
const BASE = '/api/v1/desk';

async function raw<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  extra?: RequestInit,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        accept: 'application/json',
        ...(method === 'POST' && !(body instanceof Blob)
          ? { 'content-type': 'application/json', 'x-hopex-request': '1' }
          : {}),
        ...(body instanceof Blob ? { 'x-hopex-request': '1' } : {}),
        ...(extra?.headers as Record<string, string> | undefined),
      },
      body: method === 'POST' ? (body instanceof Blob ? body : JSON.stringify(body ?? {})) : null,
    });
  } catch {
    throw new ApiFailure('OFFLINE', OFFLINE, 0);
  }
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const e = (json as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiFailure(e?.code ?? 'INTERNAL', e?.message ?? OFFLINE, res.status);
  }
  return json as T;
}

let signing: Promise<Me | null> | null = null;

/** Challenge–response sign-in with the stored device key (deduplicated across parallel callers). */
export function signInWithDevice(): Promise<Me | null> {
  signing ??= (async () => {
    try {
      const d = await loadDevice();
      if (!d) return null;
      const { challenge } = await raw<{ challenge: string }>('POST', '/auth/challenge', {
        deviceId: d.deviceId,
      });
      const signature = await signChallenge(d.key, challenge);
      return await raw<Me>('POST', '/auth/verify', { deviceId: d.deviceId, challenge, signature });
    } catch (e) {
      // The device was revoked / expired: forget it so the pairing screen shows.
      if (e instanceof ApiFailure && e.code === 'DESK_DEVICE_INVALID') await forgetDevice();
      throw e;
    } finally {
      signing = null;
    }
  })();
  return signing;
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  extra?: RequestInit,
): Promise<T> {
  try {
    return await raw<T>(method, path, body, extra);
  } catch (e) {
    if (e instanceof ApiFailure && e.status === 401 && !path.startsWith('/auth/')) {
      const me = await signInWithDevice();
      if (me) return raw<T>(method, path, body, extra);
    }
    throw e;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T = { ok: true }>(path: string, body?: unknown) => request<T>('POST', path, body),
  upload: <T>(path: string, file: File) =>
    request<T>('POST', path, file, {
      headers: {
        'content-type': file.type,
        'x-file-name': encodeURIComponent(file.name).slice(0, 380),
      },
    }),
  raw,
};

export const errorMessage = (e: unknown): string => (e instanceof ApiFailure ? e.message : OFFLINE);

/**
 * OD-71 operations: the panel's own commands through /api/v1/desk/ops/*. A command the server gates
 * with a step-up is proven by a FRESH signature of this device's key (silent — nothing to type), then
 * retried once with the SAME Idempotency-Key, so a retry can never act twice.
 */
async function deviceStepUp(): Promise<void> {
  const d = await loadDevice();
  if (!d) throw new ApiFailure('DESK_DEVICE_INVALID', 'این دستگاه دیگر متصل نیست.', 401);
  const { challenge } = await raw<{ challenge: string }>('POST', '/auth/challenge', {
    deviceId: d.deviceId,
  });
  const signature = await signChallenge(d.key, challenge);
  await request('POST', '/auth/step-up', { challenge, signature });
}

/** POST that answers with bytes (KYC2 evidence): same session, CSRF header and error envelope. */
async function rawBlob(path: string, body: unknown): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'content-type': 'application/json', 'x-hopex-request': '1' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiFailure('OFFLINE', OFFLINE, 0);
  }
  if (!res.ok) {
    let e: { code: string; message: string } | undefined;
    try {
      e = ((await res.json()) as { error?: { code: string; message: string } }).error;
    } catch {
      e = undefined;
    }
    throw new ApiFailure(e?.code ?? 'INTERNAL', e?.message ?? OFFLINE, res.status);
  }
  return res.blob();
}

let steppedUpAt = 0;
export const ops = {
  /** Private evidence bytes (never a URL); each view is audited server-side. */
  blob: async (path: string, body: unknown): Promise<Blob> => {
    if (Date.now() - steppedUpAt > 19 * 60_000) {
      await deviceStepUp();
      steppedUpAt = Date.now();
    }
    try {
      return await rawBlob(`/ops/${path}`, body);
    } catch (e) {
      if (e instanceof ApiFailure && e.status === 401) {
        await signInWithDevice();
        return rawBlob(`/ops/${path}`, body);
      }
      if (e instanceof ApiFailure && e.code === 'STEP_UP_REQUIRED') {
        await deviceStepUp();
        steppedUpAt = Date.now();
        return rawBlob(`/ops/${path}`, body);
      }
      throw e;
    }
  },
  get: <T>(path: string) => request<T>('GET', `/ops/${path}`),
  run: async <T = unknown>(path: string, body: unknown): Promise<T> => {
    // Sign first when the last proof is older than the server's 20-minute window (no refusal round trip).
    if (Date.now() - steppedUpAt > 19 * 60_000) {
      await deviceStepUp();
      steppedUpAt = Date.now();
    }
    const key = crypto.randomUUID().replace(/-/g, '');
    const send = () =>
      request<T>('POST', `/ops/${path}`, body, { headers: { 'idempotency-key': key } });
    try {
      return await send();
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'STEP_UP_REQUIRED') {
        await deviceStepUp();
        steppedUpAt = Date.now();
        return send();
      }
      throw e;
    }
  },
};
